import type {
  SubscriptionDetails,
  V2BillingTier,
} from "schemas/generated/billing";

import * as React from "react";

import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";
import { billingRouter } from "@editor/trpc/billing";

import Separator from "@replo/design-system/components/Separator";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@replo/design-system/components/shadcn/core/card";
import { Tag } from "@replo/design-system/components/tag/Tag";
import { skipToken } from "@tanstack/react-query";
import { ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import { formatPrice } from "replo-utils/lib/math";
import { pluralize } from "replo-utils/lib/pluralize";
import { capitalizeFirstLetter } from "replo-utils/lib/string";
import {
  getV1BillingPlanInfoByTier,
  getV2BillingPlanInfoByTier,
  v1AdditionalPublishedElementBundleSize,
} from "schemas/billing";

import {
  getCountOfAdditionalPublishedElementBundlesInSubscription,
  getCountOfV1AdditionalShopsInSubscription,
} from "../v1/v1BillingUtils";
import {
  getV1PlanPricePerMonthIncludingAllProducts,
  V1PlanPricePerMonthIncludingAllProducts,
} from "../v1/V1PlanPrice";
import { getCountOfV2AdditionalShopsInSubscription } from "./V2BillingUtils";
import {
  getV2PlanPricePerMonthIncludingAllProducts,
  V2PlanPricePerMonthIncludingAllProducts,
} from "./V2PlanPrice";

const V2BillingSummary = ({
  activeSubscription,
  isNewPlanYearly,
  newV2Tier,
  newV2AdditionalShops,
}: {
  activeSubscription?: SubscriptionDetails;
  isNewPlanYearly: boolean;
  newV2Tier: V2BillingTier;
  newV2AdditionalShops: number;
}) => {
  const workspaceId = useCurrentWorkspaceId();

  const [isBillingSummaryOpen, setIsBillingSummaryOpen] = React.useState(false);

  const { data: nextBillingDate } = billingRouter.getNextBillingDate.useQuery(
    workspaceId && activeSubscription?.id
      ? {
          workspaceId,
          subscriptionId: activeSubscription.id,
        }
      : skipToken,
  );

  const currentV2Tier = activeSubscription?.v2BillingTier ?? "free";
  const isCurrentPlanYearly = activeSubscription?.billingInterval === "yearly";
  const currentV2AdditionalShops = activeSubscription
    ? getCountOfV2AdditionalShopsInSubscription(activeSubscription)
    : 0;

  const currentV2PlanInfo = getV2BillingPlanInfoByTier(currentV2Tier);
  const newV2PlanInfo = getV2BillingPlanInfoByTier(newV2Tier);

  const currentV1Tier = activeSubscription?.v1BillingTier ?? "free";
  const currentV1AdditionalShops = activeSubscription
    ? getCountOfV1AdditionalShopsInSubscription(activeSubscription)
    : 0;
  const currentAdditionalPublishedElementBundles = activeSubscription
    ? getCountOfAdditionalPublishedElementBundlesInSubscription(
        activeSubscription,
      )
    : 0;
  const currentAdditionalPublishedElements =
    currentAdditionalPublishedElementBundles *
    v1AdditionalPublishedElementBundleSize;

  const currentV1PlanInfo = getV1BillingPlanInfoByTier(currentV1Tier);

  const nextBillingDateToShowUser = React.useMemo(() => {
    if (nextBillingDate) {
      return new Date(nextBillingDate).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    } else {
      const futureDate = new Date();
      if (isNewPlanYearly) {
        futureDate.setFullYear(futureDate.getFullYear() + 1);
      } else {
        futureDate.setMonth(futureDate.getMonth() + 1);
      }
      return futureDate.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      });
    }
  }, [nextBillingDate, isNewPlanYearly]);

  const totalPricePerMonthOfCurrentWorkspaceSubscription =
    getV2PlanPricePerMonthIncludingAllProducts({
      plan: currentV2PlanInfo,
      billingInterval: isCurrentPlanYearly ? "yearly" : "monthly",
      v2AdditionalShops: currentV2AdditionalShops,
    }) +
    getV1PlanPricePerMonthIncludingAllProducts({
      plan: currentV1PlanInfo,
      billingInterval: isCurrentPlanYearly ? "yearly" : "monthly",
      additionalShops: currentV1AdditionalShops,
      additionalPublishedElementBundles:
        currentAdditionalPublishedElementBundles,
    });

  const totalPricePerMonthOfNewWorkspaceSubscription =
    getV2PlanPricePerMonthIncludingAllProducts({
      plan: newV2PlanInfo,
      billingInterval: isNewPlanYearly ? "yearly" : "monthly",
      v2AdditionalShops: newV2AdditionalShops,
    }) +
    getV1PlanPricePerMonthIncludingAllProducts({
      plan: currentV1PlanInfo,
      billingInterval: isNewPlanYearly ? "yearly" : "monthly",
      additionalShops: currentV1AdditionalShops,
      additionalPublishedElementBundles:
        currentAdditionalPublishedElementBundles,
    });

  return (
    <Card>
      <CardHeader
        className="cursor-pointer"
        onClick={() => setIsBillingSummaryOpen(!isBillingSummaryOpen)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="header-h4">Workspace Billing Summary</CardTitle>
          {isBillingSummaryOpen ? (
            <ChevronUp size={24} />
          ) : (
            <ChevronDown size={24} />
          )}
        </div>
      </CardHeader>
      {isBillingSummaryOpen && (
        <CardContent>
          {/* V1 Billing Summary */}
          {currentV1Tier !== "free" && (
            <div className="flex justify-between items-center">
              <div className="flex flex-col gap-1">
                <div className="text-base font-medium">
                  Replo for Shopify Themes -{" "}
                  {capitalizeFirstLetter(currentV1Tier)}
                </div>
                <div className="text-sm text-muted">
                  {currentV1AdditionalShops + 1}{" "}
                  {pluralize({
                    singular: "Shop",
                    plural: "Shops",
                    count: currentV1AdditionalShops + 1,
                  })}
                  {currentAdditionalPublishedElements > 0 && (
                    <>
                      {" "}
                      • {currentAdditionalPublishedElements} Additional
                      Published Elements
                    </>
                  )}
                </div>
              </div>
              <V1PlanPricePerMonthIncludingAllProducts
                plan={currentV1PlanInfo}
                billingInterval={isNewPlanYearly ? "yearly" : "monthly"}
                additionalShops={currentV1AdditionalShops}
                additionalPublishedElementBundles={
                  currentAdditionalPublishedElementBundles
                }
                variant="base"
              />
            </div>
          )}

          {/* V2 Billing Summary */}
          <div className="flex justify-between items-center">
            <div className="flex flex-col gap-1">
              <div className="text-base font-medium">
                Replo AI Builder - {capitalizeFirstLetter(newV2Tier)}
              </div>
              <div className="text-sm text-muted">
                {newV2AdditionalShops + 1}{" "}
                {pluralize({
                  singular: "Shop",
                  plural: "Shops",
                  count: newV2AdditionalShops + 1,
                })}
              </div>
            </div>
            <div className="flex gap-2 items-center">
              {currentV2Tier !== "free" && (
                <>
                  <div className="text-muted">
                    <V2PlanPricePerMonthIncludingAllProducts
                      plan={currentV2PlanInfo}
                      billingInterval={
                        isCurrentPlanYearly ? "yearly" : "monthly"
                      }
                      v2AdditionalShops={currentV2AdditionalShops}
                      variant="base"
                    />
                  </div>
                  <ArrowRight className="text-muted" size={18} />
                </>
              )}

              <V2PlanPricePerMonthIncludingAllProducts
                plan={newV2PlanInfo}
                billingInterval={isNewPlanYearly ? "yearly" : "monthly"}
                v2AdditionalShops={newV2AdditionalShops}
                variant="base"
              />
            </div>
          </div>

          <Separator />

          {/* New Monthly Total */}
          <div className="flex justify-between items-center">
            <div className="flex flex-col gap-1">
              <div className="flex gap-2 items-center">
                <div className="text-base font-medium">New Monthly Total</div>
                <Tag size="sm" variant="v2">
                  Billed {isNewPlanYearly ? "Annually" : "Monthly"}
                </Tag>
              </div>
              <div className="text-sm text-muted">
                Renews {nextBillingDateToShowUser}
              </div>
            </div>
            <div className="flex gap-2 items-center">
              <div className="text-lg font-medium text-muted">
                {formatPrice(totalPricePerMonthOfCurrentWorkspaceSubscription)}
                /month
              </div>

              <ArrowRight className="text-muted" size={18} />

              <div className="text-lg font-medium">
                {formatPrice(totalPricePerMonthOfNewWorkspaceSubscription)}
                /month
              </div>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default V2BillingSummary;
